// Screen · Team (Lead) — route /team (the old /status address opens it too). 2026-09-30 redesign.
//
// One page for the Lead's weekly job, top to bottom: the plans waiting for review; every class this
// week (its lesson, its plan status, whether it is on pace with the year plan); every teacher, with
// a way into their page — INCLUDING teachers who have no class or no plan yet (before, Insights only
// listed teachers who already had plans, so an observation could not even be started for them).
// Neutral by construction (CLAUDE.md rails): no ranking, no red, sorted by grade, never by "misses";
// pace figures are planning aids for conversations, never published to the team.

import { api } from "../api";
import { esc } from "../dom";
import { icon } from "../icons";
import { nodeForWeek, termNumber, type CurriculumNode, type GradeCurriculum } from "../curriculum";
import { resolveClassLesson, byGrade, termPhase, termWeekLabel, termNote, type ClassLesson, type ClassRow, type TermInfo } from "../lesson-data";
import { pageHeader, statusChip, emptyState, dayLabel, rangeLabel, type PlanState } from "../ui";
import { classStateThisWeek, reviewRowHtml, teamRowsFor } from "./home";
import type { WeekClock } from "../clock";
import { loadLessonRefs, teacherTitle } from "../lesson-refs";

interface StatusRow {
  teacher_name: string;
  class_name: string;
  week_no: number;
  status: "not_started" | "in_progress" | "submitted" | "approved";
  on_time: boolean | null;
  submitted_at: string | null;
  plan_id: string | null;
}
interface TeamWeekLesson { teacher_name: string; class_name: string; topic: string; status: StatusRow["status"]; week_no?: number }
export interface AdminUser {
  id: string;
  name: string;
  email: string;
  auth_role: "teacher" | "lead";
  coaching_posture: "junior" | "regular" | "senior" | null;
  active: boolean;
}

/** Pace in plain words: the class's planned position against the year plan's step for this week. */
export function paceText(cl: ClassLesson, termInfo: TermInfo | null): { text: string; behind: number } {
  const cur = cl.cur;
  const at = cl.pos?.position ?? null;
  if (!cur) return { text: "—", behind: 0 };
  if (!at) return { text: "No linked plans yet", behind: 0 };
  const termN = termInfo?.is_current ? termNumber(termInfo.term) : null;
  const week = termInfo?.is_current ? termInfo.current_week : null;
  const tpl: CurriculumNode | null = termN && week ? nodeForWeek(cur, termN, week) : null;
  if (!tpl) return { text: `Planned to week ${at.suggested_week} of Term ${at.term}`, behind: 0 };
  const d = tpl.seq - at.seq;
  if (d <= -1) return { text: `${-d} week${d === -1 ? "" : "s"} ahead`, behind: d };
  if (d <= 1) return { text: "On pace", behind: d };
  return { text: `${d} weeks behind`, behind: d };
}

/** Weeks that can merge to make room (buffer weeks first, then review weeks) — a coaching aid. */
function catchUp(cur: GradeCurriculum, fromSeq: number): CurriculumNode[] {
  const ahead = cur.nodes.filter((n) => n.seq > fromSeq);
  return [...ahead.filter((n) => n.type === "buffer"), ...ahead.filter((n) => n.type === "review")].slice(0, 6);
}

export async function renderTeam(page: HTMLElement): Promise<void> {
  const [termInfo, clock, status, team, all, users] = await Promise.all([
    api<TermInfo>("/api/terms/current").catch(() => null),
    api<WeekClock>("/api/clock").catch(() => null),
    api<{ rows: StatusRow[] }>("/api/lead/status"),
    api<{ lessons: TeamWeekLesson[]; week_no: number | null }>("/api/dashboard").catch(() => ({ lessons: [] as TeamWeekLesson[], week_no: null })),
    api<{ classes: ClassRow[] }>("/api/classes"),
    api<{ users: AdminUser[] }>("/api/admin/users").catch(() => ({ users: [] as AdminUser[] })),
    loadLessonRefs(), // week titles name the unit
  ]);
  const clockOk = clock && "week" in clock ? clock : null;
  const classes = [...all.classes].sort(byGrade);
  const lessons = await Promise.all(classes.map((c) => resolveClassLesson(c, termInfo)));

  // ── waiting for review ──
  const toReview = status.rows
    .filter((r) => r.status === "submitted" && r.plan_id)
    .sort((a, b) => (a.submitted_at ?? "").localeCompare(b.submitted_at ?? ""));
  const review = toReview.length
    ? `<div class="list">${toReview.map(reviewRowHtml).join("")}</div>`
    : emptyState("Nothing is waiting — every plan sent to you has been reviewed.");
  // The last few plans approved — where a plan goes after Approve, and the way back to re-open one.
  const approvedRecent = status.rows
    .filter((r) => r.status === "approved" && r.plan_id)
    .sort((a, b) => (b.submitted_at ?? "").localeCompare(a.submitted_at ?? ""))
    .slice(0, 3);
  const approvedList = approvedRecent.length
    ? `<h3 class="sub-title">Approved recently</h3><div class="list">${approvedRecent.map((r) => `<a class="row-link" data-link href="/plan/${esc(r.plan_id ?? "")}">
        <span class="rl-main"><b>${esc(r.teacher_name)}</b> · ${esc(r.class_name)} · Week ${esc(r.week_no)}</span>
        <span class="rl-meta">${statusChip("approved")}</span>${icon("chevron", "ico-sm")}
      </a>`).join("")}</div>`
    : "";

  // ── this week, class by class ──
  const rows = lessons.map((cl) => {
    const c = cl.cls;
    const s: PlanState = classStateThisWeek(c, team.lessons, classes);
    const pace = paceText(cl, termInfo);
    // A class that has planned this week shows its plan's own name (the plan the status chip is about);
    // one that has not shows the lesson the year plan has next for it.
    const planned = teamRowsFor(c, team.lessons, classes);
    // each planned lesson opens its plan (the Lead's status rows carry the plan id the team week leaves out)
    // — linked only when exactly one plan fits (two same-status plans in one week stay plain text:
    // a wrong link is worse than none)
    const planLink = (l: TeamWeekLesson): string => {
      const fits = status.rows.filter((r) => r.plan_id && r.class_name === l.class_name && r.teacher_name === l.teacher_name
        && r.status === l.status && (l.week_no == null || r.week_no === l.week_no));
      const same = planned.filter((x) => x.status === l.status).length;
      if (fits.length !== 1 || same !== 1) return esc(l.topic);
      return `<a data-link href="/plan/${encodeURIComponent(fits[0]!.plan_id!)}">${esc(l.topic)}</a>`;
    };
    const lessonCell = planned.length
      ? planned.map(planLink).join(" · ")
      : cl.node
        ? `<a data-link href="/map/${encodeURIComponent(cl.node.id)}?class=${encodeURIComponent(c.id)}">${esc(teacherTitle(cl.node))}</a>${cl.noStep ? ` <span class="muted small">(no new lesson this week)</span>` : ""}`
        : `<span class="muted">${cl.grade ? "Not in the year plan yet" : "Grade not in the class name"}</span>`;
    const behind = pace.behind >= 2 && cl.cur && cl.pos?.position
      ? `<details class="mini-fold"><summary>Catch-up options</summary><p class="muted small">Weeks that can merge to make room — buffer weeks first, then review weeks. Cambridge practice continues regardless.</p><ul>${catchUp(cl.cur, cl.pos.position.seq).map((n) => `<li>Term ${n.term} · Week ${n.suggested_week} — ${esc(teacherTitle(n))}</li>`).join("")}</ul></details>`
      : "";
    return `<tr data-class-row="${esc(c.id)}">
      <td><a data-link href="/roadmap?class=${encodeURIComponent(c.id)}"><b>${esc(c.name)}</b></a></td>
      <td>${c.teacher_id ? `<a data-link href="/team/${esc(c.teacher_id)}">${esc(c.teacher_name ?? "")}</a>` : "—"}</td>
      <td class="td-lesson">${lessonCell}</td>
      <td>${statusChip(s, { lead: true })}</td>
      <td class="td-pace${pace.behind >= 2 ? " behind" : ""}">${esc(pace.text)}${behind}</td>
    </tr>`;
  }).join("");
  const table = classes.length
    ? `<div class="table-wrap"><table class="tbl tbl-team">
        <thead><tr><th>Class</th><th>Teacher</th><th>This week's lesson</th><th>Plan</th><th>Pace</th></tr></thead>
        <tbody>${rows}</tbody></table></div>
       <p class="muted small">Pace compares the weeks a class has planned with the year plan. It is a starting point for a conversation — never a ranking.</p>`
    : emptyState("No classes yet. Add the department's classes in Setup.", `<a class="btn secondary" data-link href="/admin?tab=classes">${icon("setup")}<span>Set up classes</span></a>`);

  // Exam dates for gate years (registration + the sitting), once per grade.
  const seenGrades = new Set<number>();
  const gateLines = lessons.filter((cl) => cl.cur?.gate && cl.grade && !seenGrades.has(cl.grade) && seenGrades.add(cl.grade)).map((cl) => {
    const pins = cl.cur!.anchors.filter((a) => a.kind === "gate" || a.kind === "registration");
    return pins.length ? `<li><b>Grade ${cl.grade}</b> — ${pins.map((a) => `${esc(a.label)} (Term ${a.window.term}, weeks ${a.window.weeks[0]}–${a.window.weeks[1]})`).join("; ")}</li>` : "";
  }).join("");
  const gates = gateLines ? `<details class="card fold"><summary>${icon("flag")}<span>Cambridge exam dates for this year's exam grades</span>${icon("down", "ico-sm fold-caret")}</summary><div class="fold-body"><ul class="gate-list">${gateLines}</ul></div></details>` : "";

  // ── teachers (everyone active — with or without a class or a plan) ──
  const people = users.users.filter((u) => u.active).sort((a, b) => a.name.localeCompare(b.name));
  const teacherCards = people.map((u) => {
    const theirs = classes.filter((c) => c.teacher_id === u.id);
    const badges = theirs.map((c) => {
      const s = classStateThisWeek(c, team.lessons, classes);
      return `<span class="class-pill st-${s === "in_progress" || s === "not_started" ? "draft" : s}"><b>${esc(c.name)}</b> ${s === "none" ? "not planned" : s === "in_progress" || s === "not_started" ? "draft" : s === "submitted" ? "to review" : "approved"}</span>`;
    }).join("");
    return `<a class="person" data-link href="/team/${esc(u.id)}" data-teacher="${esc(u.id)}">
      <span class="avatar" aria-hidden="true">${esc(u.name.slice(0, 1).toUpperCase())}</span>
      <span class="person-main">
        <span class="person-name">${esc(u.name)}${u.auth_role === "lead" ? ` <span class="muted small">Lead</span>` : ""}</span>
        <span class="person-classes">${badges || `<span class="muted small">No class yet</span>`}</span>
      </span>
      ${icon("chevron", "ico-sm")}
    </a>`;
  }).join("");
  const noClass = people.filter((u) => u.auth_role === "teacher" && !classes.some((c) => c.teacher_id === u.id));

  const inTerm = !!termInfo && termPhase(termInfo) === "in";
  const eyebrow = termInfo ? termWeekLabel(termInfo, clockOk ? rangeLabel(clockOk.week.start, clockOk.week.end) : "") : "";
  const sub = clockOk && inTerm
    ? `This week's plans are due ${esc(dayLabel(clockOk.deadline))}.`
    : termInfo && !inTerm ? esc(termNote(termInfo)) : "Plans waiting for you, and how each class is moving through the year.";

  page.innerHTML = `
    ${pageHeader({
      eyebrow,
      title: "Team",
      sub,
      actions: `<a class="btn secondary" data-link data-lead-teaching-link href="/lead/teaching">${icon("plans")}<span>Teaching content and used materials</span></a><a class="btn secondary" data-link href="/lead/progress">${icon("plans")}<span>Progress and saved activity</span></a><a class="btn secondary" data-link href="/insights">${icon("plans")}<span>Term report</span></a>`,
    })}
    <nav class="jump" aria-label="On this page">
      <a href="#review">To review <span class="count">${toReview.length}</span></a>
      <a href="#week">This week</a>
      <a href="#teachers">Teachers <span class="count">${people.length}</span></a>
    </nav>
    <h2 class="sec-title" id="review">Waiting for your review</h2>
    ${review}
    ${approvedList}
    <h2 class="sec-title" id="week">${inTerm ? "This week" : `Week ${termInfo && termPhase(termInfo) === "before" ? 1 : termInfo?.current_week ?? ""}`}, class by class</h2>
    ${table}
    ${gates}
    <h2 class="sec-title" id="teachers">Teachers</h2>
    ${noClass.length ? `<p class="note">${icon("info")}<span>${noClass.length === 1 ? `${esc(noClass[0]!.name)} has` : `${noClass.length} teachers have`} no class yet, so their Home is empty. <a data-link href="/admin?tab=classes">Assign classes in Setup</a>.</span></p>` : ""}
    <div class="people">${teacherCards || emptyState("No teacher accounts yet.", `<a class="btn secondary" data-link href="/admin?tab=people">Add teachers</a>`)}</div>`;

  // In-page jump links scroll smoothly without changing the route.
  page.querySelectorAll<HTMLAnchorElement>(".jump a").forEach((a) => a.addEventListener("click", (e) => {
    e.preventDefault();
    page.querySelector(a.getAttribute("href")!)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }));
  page.addEventListener("eden:mounted", () => {
    if (location.hash) page.querySelector(location.hash)?.scrollIntoView({ block: "start" });
  }, { once: true });
}
